import { useState, useEffect } from 'react'
import SunEditor from 'suneditor-react'
import 'suneditor/dist/css/suneditor.min.css'
import { Button, message, Modal } from '@pankod/refine-antd'
import LatexEquationEditorModal from './latex_eqEditor_modal'
import { dataUrlToFile, unsupportedMediaMessage, uploadQuestionMedia } from 'helpers/question/media'
import { AUDIO_ACCEPT_ATTRIBUTE, VIDEO_ACCEPT_ATTRIBUTE } from 'constants/mediaUpload'
import type { UploadBeforeHandler, UploadBeforeReturn } from 'suneditor-react/dist/types/upload'
import katex from 'katex'
import 'katex/dist/katex.min.css'

interface SunTextEditorProps {
  id: any
  initialValue?: string
  formValueSetterFn?: (content: string) => void
  hideToolBar?: boolean
}

type BlobInfo = {
  id: () => any
  name: () => string
  filename: () => string
  blob: () => Blob
  base64: () => string
  blobUri: () => string
  uri: () => string | undefined
}
type ProgressFn = (percent: number) => void
export type UploadHandler = (blobInfo: BlobInfo, progress: ProgressFn) => Promise<string>

const SunTextEditor: React.FC<SunTextEditorProps> = ({ id, initialValue, formValueSetterFn, hideToolBar }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const handleImageUpload = async (imageTag: HTMLImageElement) => {
    let url = ''
    try {
      setLoading(true)
      const src = imageTag.src
      if (src.startsWith('data:')) {
        // GIFs reach here too now; uploadQuestionMedia skips compression for them so
        // the animation survives (canvas re-encoding would flatten it to one frame).
        const file = dataUrlToFile(src)
        if (!file) {
          imageTag.src = url
          message.error(unsupportedMediaMessage())
          throw new Error('Could not read the pasted image data')
        }

        const result = await uploadQuestionMedia(file)
        if (!result.ok) {
          imageTag.src = url
          message.error(result.message)
          throw new Error(result.message)
        }

        url = result.url
        imageTag.src = url
      } else {
        url = src
      }
    } catch (err) {
      console.log({ err })
    }

    setLoading(false)
    return Promise.resolve(url)
  }

  /**
   * Routes SunEditor's audio/video pickers to nhost instead of its default upload URL.
   * Returning undefined tells SunEditor the upload is async and it should wait for
   * uploadHandler; returning false would cancel insertion outright.
   */
  const handleMediaUploadBefore = (files: File[], _info: object, uploadHandler: UploadBeforeHandler): UploadBeforeReturn => {
    ;(async () => {
      setLoading(true)
      try {
        const uploaded = []
        for (const file of files) {
          const result = await uploadQuestionMedia(file)
          if (!result.ok) {
            message.error(result.message)
            uploadHandler(result.message)
            return
          }
          uploaded.push({ url: result.url, name: result.name, size: file.size })
        }
        uploadHandler({ result: uploaded })
      } catch (err) {
        console.log({ err })
        const failureMessage = 'Upload failed. Please try again.'
        message.error(failureMessage)
        uploadHandler(failureMessage)
      } finally {
        setLoading(false)
      }
    })()

    return undefined
  }

  return (
    <div id={id}>
      {!hideToolBar && <LatexEquationEditorModal />}
      <SunEditor
        height="300"
        defaultValue={initialValue}
        autoFocus={false}
        readOnly={hideToolBar ? true : false}
        hideToolbar={hideToolBar ? true : false}
        onChange={value => {
          if (formValueSetterFn) formValueSetterFn(value)
        }}
        onImageUpload={handleImageUpload}
        onAudioUploadBefore={handleMediaUploadBefore}
        onVideoUploadBefore={handleMediaUploadBefore}
        setOptions={{
          katex: katex,
          audioAccept: AUDIO_ACCEPT_ATTRIBUTE,
          videoAccept: VIDEO_ACCEPT_ATTRIBUTE,
          // Upload to nhost only; the URL/iframe tab would embed third-party players
          // that the exam delivery platform cannot be expected to render offline.
          videoFileInput: true,
          videoUrlInput: false,
          buttonList: [
            ['undo', 'redo'],
            ['font', 'fontSize', 'formatBlock'],
            ['paragraphStyle', 'blockquote'],
            ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
            ['fontColor', 'hiliteColor', 'textStyle'],
            ['removeFormat', 'preview'],
            '/',
            ['outdent', 'indent'],
            ['align', 'horizontalRule', 'list', 'lineHeight'],
            ['table', 'link', 'image', 'audio', 'video', 'math'],
            ['fullScreen', 'codeView'],
          ],
        }}
      />
    </div>
  )
}

export default SunTextEditor
